import styled from "@emotion/styled";
import { ReactNode } from "react";
import { SearchResultDataValue } from "@kaltura/mediaspace-shared-types";
import { Tooltip, Typography } from "@kaltura/ds-react-components";
import { formatters, useMediaQuery } from "@kaltura/mediaspace-shared-utils";
import { useTheme } from "@kaltura/mediaspace-shared-styled";

export interface SearchResultItemProps {
    /**
     * icon to show
     */
    icon: ReactNode;

    /**
     * text to show
     */
    label: string;

    /**
     * complete list of values, max 5 will show on tooltip
     */
    values: SearchResultDataValue[];

    /**
     * callback for when the item is clicked
     */
    onClick: () => void;

    /**
     * should time info be shown on the tooltip, assuming data exists
     */
    isTimed: boolean;
}

const StyledSearchResultItem = styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    color: theme.kaltura.palette.tone2,
    cursor: "pointer",
}));

const StyledUl = styled("ul")({
    listStyleType: "none",
    margin: 0,
    padding: 0,
});

/**
 * single search result item
 */
export function SearchResultItem({ icon, label, values, onClick, isTimed }: SearchResultItemProps) {
    const theme = useTheme();

    const isSmallScreen = !useMediaQuery(theme.breakpoints.up(theme.breakpoints.values.md));

    const getTooltip = (isTimed: boolean, values: SearchResultDataValue[]) => {
        return (
            <StyledUl>
                {values
                    .slice(0, 5)
                    .map((value, index) => {
                        const text = value.text.replaceAll("<em>", "").replaceAll("</em>", "");
                        let time = "";
                        if (isTimed && value.startTime !== undefined) {
                            time = formatters.formatSecondsToTime(value.startTime / 1000) + " - ";
                        }

                        return (
                            <li key={`tl${index}`}>
                                {time}
                                {text}
                            </li>
                        );
                    })}
            </StyledUl>
        );
    };

    return (
        <Tooltip title={isSmallScreen ? label : getTooltip(isTimed, values)}>
            <StyledSearchResultItem
                onClick={() => onClick()}
                tabIndex={0}
                role={"button"}
                className="kms-ds-media-card-info-search-results-item"
            >
                {icon}
                {!isSmallScreen && <Typography variant={"body2Highlight"}>{label}</Typography>}
            </StyledSearchResultItem>
        </Tooltip>
    );
}

export default SearchResultItem;
